import {
  CSSProperties,
  FC,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useState,
} from "react";
import { useHistory } from "react-router";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { Tooltip, TooltipProps } from "../Tooltip/Tooltip";
import { SortDirection } from "@/utils/api";
import {
  SortActiveDesc,
  SortInactive,
} from "@/components/icons/svg/figma/Sort";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

const STICKY_TABLE_HEAD_CLASSES =
  "outline-dividerBold bg-surface-default sticky top-0 left-0 z-50 rounded-tl-lg rounded-tr-lg leading-none outline-1";
const TABLE_HEAD_CLASSES =
  "border-dividerDefault bg-surface-default border-b leading-none";

/**
 * Sticky <thead> with the canonical Freshpaint table-header styling: opaque
 * background, hairline outline that stays attached to the header as the body
 * scrolls, and dark-mode parity. Use this anywhere a hand-rolled <table> needs
 * a sticky header so the styling stays in lockstep with the design-system
 * <Table> component.
 */
export function StickyTableHead({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={twMerge(clsx(STICKY_TABLE_HEAD_CLASSES, className))}
      {...rest}
    >
      {children}
    </thead>
  );
}

export type CellProps<RowType> = {
  row: RowType;
  index: number;
};

type CellClassNameProps<RowType> = {
  row: RowType;
  index: number;
};

export type ColumnConfig<RowType, SortKey = undefined> = {
  label: string;
  sortKey?: SortKey;
  align?: CSSProperties["textAlign"];
  width?: CSSProperties["width"];
  tooltipProps?: TooltipProps;
  getCellClassName?: (props: CellClassNameProps<RowType>) => string | undefined;
  HeaderCellContent?: FC<{
    config: ColumnConfig<RowType, SortKey>;
  }>;
} & (
  | {
      CellContent: FC<CellProps<RowType>>;
    }
  | {
      getCellText: (row: RowType) => string | number;
    }
);

interface TableProps<RowType, SortKey = undefined> {
  rows: RowType[];
  getRowId: (row: RowType) => string;
  columnConfigs: ColumnConfig<RowType, SortKey>[];
  EmptyState: FC;

  className?: string;
  tableClassName?: string;
  loading?: boolean;
  rowLinkHref?: (row: RowType) => string | undefined;
  rowLinkTarget?: React.HTMLAttributeAnchorTarget;
  /**
   * When provided alongside rowLinkHref, onRowClick handles row activation
   * and rowLinkHref only supplies the link semantics (data-href, role).
   * Use the combination when navigation needs side effects rowLinkHref's
   * bare push can't express, e.g. stamping history state.
   */
  onRowClick?: (row: RowType) => void;
  footerContent?: ReactNode;
  tfootContent?: ReactNode;
  sortBy?: SortKey;
  sortDir?: SortDirection;
  onSortChange?: (sortBy: SortKey, sortDir: SortDirection) => void;
  tracking?: string;
  stickyHeader?: boolean;
  stickyHeaderTop?: CSSProperties["top"];
  stickyFirstColumn?: boolean;
  /**
   * Marks body rows that should remain pinned while scrolling. Sticky row cells
   * are pinned below the table header by default, so this pairs naturally with
   * stickyHeader for grouped tables.
   */
  isStickyRow?: (props: CellProps<RowType>) => boolean;
  stickyRowTop?: CSSProperties["top"];
  /**
   * When true, omits the surrounding card wrapper (border, rounded corners,
   * shadow, background). Use when the table is composed inside an existing
   * card or container that already provides those visuals — otherwise you'll
   * end up with a double-card. Defaults to false.
   */
  bare?: boolean;
  /**
   * Escape hatch for rows that don't fit the normal per-column rendering
   * (e.g. full-width banners or callouts spanning every column). When this
   * returns a ReactNode, it replaces the default cells with a single
   * column-spanning <td>. Returning null or undefined falls back to default
   * column rendering.
   */
  renderFullWidthRow?: (props: CellProps<RowType>) => ReactNode;
  /**
   * Optional per-row class hook applied to the <tr>. Useful for overriding
   * row-level styling such as the default `divide-y` divider (e.g. removing
   * the divider between grouped child rows).
   */
  getRowClassName?: (props: CellProps<RowType>) => string | undefined;
}

export function Table<RowType, SortKey = undefined>({
  className,
  tableClassName,
  loading,
  columnConfigs,
  EmptyState,
  rows,
  getRowId,
  footerContent,
  tfootContent,
  rowLinkHref,
  rowLinkTarget,
  onRowClick,
  sortBy,
  sortDir,
  onSortChange,
  tracking,
  stickyHeader = false,
  stickyHeaderTop = 0,
  stickyFirstColumn = false,
  isStickyRow,
  stickyRowTop = 0,
  bare = false,
  renderFullWidthRow,
  getRowClassName,
}: TableProps<RowType, SortKey>) {
  const navigate = useHistory().push;
  const handleRowClick = useCallback<MouseEventHandler<HTMLTableRowElement>>(
    (e) => {
      const href = e.currentTarget.dataset.href;
      if (!href) {
        console.warn("No href on table row");
        return;
      }
      if (rowLinkTarget === "_blank") {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(href);
    },
    [navigate, rowLinkTarget],
  );
  const handleColumnClick = useCallback<
    MouseEventHandler<HTMLTableCellElement>
  >(
    (e) => {
      const columnKey = e.currentTarget.dataset.columnKey as
        | SortKey
        | undefined;
      if (!columnKey) {
        console.warn("No column key on the header");
        return;
      }

      if (sortBy === columnKey) {
        const nextSortDir = sortDir === "desc" ? "asc" : "desc";
        onSortChange?.(columnKey, nextSortDir);
      } else {
        onSortChange?.(columnKey, "desc");
      }
    },
    [onSortChange, sortBy, sortDir],
  );
  const tableHeaderRow = (
    <tr>
      {columnConfigs.map((cc, columnIndex) => {
        return (
          <th
            key={cc.label}
            role={cc.sortKey ? "button" : "columnheader"}
            data-column-key={cc.sortKey}
            onClick={handleColumnClick}
            className={clsx(
              "text-xs font-medium",
              cc.sortKey && "cursor-pointer select-none",
              stickyFirstColumn &&
                columnIndex === 0 &&
                "bg-surface-default sticky left-0 z-[60] rounded-tl-lg rounded-tr-lg after:pointer-events-none after:absolute after:top-0 after:right-0 after:h-full after:w-px after:bg-gray-200 dark:border-gray-300",
            )}
            data-track={`${tracking}-${cc.sortKey}`}
            style={{ width: cc.width }}
          >
            <div
              className={clsx("group flex items-center p-4 whitespace-nowrap", {
                "justify-center text-center": cc.align === "center",
                "justify-start text-left": cc.align === "left",
                "justify-end text-right": cc.align === "right",
              })}
            >
              {cc.tooltipProps ? (
                <Tooltip {...cc.tooltipProps}>
                  <div
                    className={
                      cc.HeaderCellContent ? "" : "underline decoration-dotted"
                    }
                  >
                    {cc.HeaderCellContent ? (
                      <cc.HeaderCellContent config={cc} />
                    ) : (
                      <span>{cc.label}</span>
                    )}
                  </div>
                </Tooltip>
              ) : (
                <div>
                  {cc.HeaderCellContent ? (
                    <cc.HeaderCellContent config={cc} />
                  ) : (
                    <span>{cc.label}</span>
                  )}
                </div>
              )}

              {cc.sortKey && (
                <div className="text-iconMinimal group-hover:text-iconDefault">
                  {sortBy === cc.sortKey && sortDir === "desc" && (
                    <SortActiveDesc className="size-6" />
                  )}
                  {sortBy === cc.sortKey && sortDir === "asc" && (
                    <SortActiveDesc className="size-6 rotate-180" />
                  )}
                  {sortBy !== cc.sortKey && <SortInactive className="size-6" />}
                </div>
              )}
            </div>
          </th>
        );
      })}
    </tr>
  );

  return (
    <div
      className={twMerge(
        clsx(
          "flex max-h-full flex-col",
          stickyHeader ? "overflow-visible" : "overflow-hidden",
          !bare &&
            "border-borderSubtle bg-surface-default rounded-lg border shadow",
          className,
        ),
      )}
    >
      <div
        className={clsx(
          "max-h-full min-h-0 w-full flex-1",
          stickyHeader ? "overflow-visible" : "overflow-auto",
        )}
      >
        <table
          className={twMerge("w-full border-spacing-0 text-sm", tableClassName)}
        >
          {stickyHeader ? (
            <StickyTableHead style={{ top: stickyHeaderTop }}>
              {tableHeaderRow}
            </StickyTableHead>
          ) : (
            <thead className={TABLE_HEAD_CLASSES}>{tableHeaderRow}</thead>
          )}
          <tbody className="divide-dividerDefault divide-y">
            {!rows.length && !loading && (
              <tr>
                <td colSpan={columnConfigs.length}>
                  <EmptyState />
                </td>
              </tr>
            )}
            {!rows.length && loading && (
              <tr>
                <td colSpan={columnConfigs.length}>
                  <div className="w-full space-y-2 py-2 text-center">
                    <LoadingSpinner className="size-8" />
                    <div>Loading...</div>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((row, index) => {
              const rowLink = rowLinkHref?.(row);
              const isClickable = rowLink || onRowClick;
              const fullWidthContent = renderFullWidthRow?.({ row, index });
              const shouldStickRow = isStickyRow?.({ row, index }) ?? false;
              const stickyRowCellClassName =
                "bg-surface-default sticky z-40 before:pointer-events-none before:absolute before:bottom-0 before:left-0 before:h-px before:w-full before:origin-bottom before:scale-y-50 before:bg-gray-200";
              const stickyRowCellStyle = shouldStickRow
                ? { top: stickyRowTop }
                : undefined;
              return (
                <tr
                  key={getRowId(row)}
                  data-href={rowLink}
                  role={rowLink ? "link" : "row"}
                  className={clsx(
                    "group",
                    isClickable &&
                      "cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-200",
                    getRowClassName?.({ row, index }),
                  )}
                  onClick={
                    onRowClick
                      ? () => onRowClick(row)
                      : rowLink
                        ? handleRowClick
                        : undefined
                  }
                >
                  {fullWidthContent != null ? (
                    <td
                      colSpan={columnConfigs.length}
                      className={clsx(
                        "border-0 p-0",
                        shouldStickRow && stickyRowCellClassName,
                      )}
                      style={stickyRowCellStyle}
                    >
                      {fullWidthContent}
                    </td>
                  ) : (
                    columnConfigs.map((cc, columnIndex) => (
                      <td
                        key={`${cc.label}-${getRowId(row)}`}
                        className={clsx(
                          "border-0 p-4",
                          {
                            "text-center": cc.align === "center",
                            "text-left": cc.align === "left",
                            "text-right": cc.align === "right",
                          },
                          shouldStickRow && stickyRowCellClassName,
                          stickyFirstColumn &&
                            columnIndex === 0 &&
                            "bg-surface-default sticky left-0 z-30 border-gray-200 group-hover:bg-gray-50 after:pointer-events-none after:absolute after:top-0 after:right-0 after:h-full after:w-px after:bg-gray-200 dark:group-hover:bg-gray-200",
                          cc.getCellClassName?.({ row, index }),
                        )}
                        style={stickyRowCellStyle}
                      >
                        {"CellContent" in cc ? (
                          <cc.CellContent row={row} index={index} />
                        ) : (
                          cc.getCellText(row)
                        )}
                      </td>
                    ))
                  )}
                </tr>
              );
            })}
          </tbody>
          {tfootContent && (
            <tfoot className="border-dividerDefault border-t bg-gray-50 font-medium">
              {tfootContent}
            </tfoot>
          )}
        </table>
      </div>

      {footerContent && (
        <div className="border-dividerDefault flex-none border-t">
          {footerContent}
        </div>
      )}
    </div>
  );
}

export function useSortByState<ColumnKey>(
  defaultSortBy?: ColumnKey,
  defaultSortDirection?: SortDirection,
) {
  const [sortBy, setSortBy] = useState<ColumnKey | undefined>(defaultSortBy);
  const [sortDir, setSortDir] = useState<SortDirection | undefined>(
    defaultSortDirection,
  );

  const handleSortChange = useCallback(
    (sortBy: ColumnKey, sortDir: SortDirection) => {
      setSortBy(sortBy);
      setSortDir(sortDir);
    },
    [],
  );

  return { sortBy, sortDir, handleSortChange };
}
